import React, { ChangeEvent, useCallback, useRef, useState } from 'react';
import { Dialog } from 'ui-components/dialog';
import { Button } from 'ui-components/button/button';
import {
    HeaderTextProps,
    MODAL_WIDTH,
    ShareModalSharedProps,
} from 'ui-components/share-report-modal/share-report-modal';
import styles from './share-modal.module.scss';
import { useGetHeaderTextPropsContent } from './share-modal-hooks';
import { useSelector } from 'react-redux';
import { selectOfflineReportsUserAccountInfo } from 'store/offline-reports/selectors/offline-reports-selectors-factory';
import { Switch } from 'ui-components/switch/switch';
import {
    OrderManagementRequest,
    SharingTargetOptions,
    UserAccountDetailsItem,
} from 'core/entities';
import { RecordType } from 'features/offline-reports/order-management/components/requests-table/requests-table';
import { Select } from 'antd';
import type { Dictionary } from '@placer-ui/types';
import { isHTMLTags } from 'utils/detect-html-tags/detect-html-tags';
import classnames from 'classnames';

type ShareModalProps = ShareModalSharedProps & {
    onShareReport: (
        sharingUsersIdList: string[],
        messageContent: string,
        sharingTarget: SharingTargetOptions,
    ) => void;
    onCancelButtonClick: () => void;
    isSendingRequest: boolean;
    record: RecordType | OrderManagementRequest;
};

type UserDetails = {
    id: string;
    username: string;
};

const MESSAGE_MAX_LENGTH = 1024;

export const ShareModal = ({
    headerContent,
    visible,
    onCloseModal,
    onCancelButtonClick,
    onShareReport,
    isSendingRequest,
    record,
}: ShareModalProps) => {
    const { sharedWith, target } = record;
    const accountShared = (target && target === 'account') || false;
    const [isUseWholeAccount, setIsUseWholeAccount] = useState<boolean>(accountShared);
    const [isRecipientsSelectionDone, setIsRecipientsSelectionDone] = useState<boolean>(false);
    const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState<boolean>(true);
    const [selectedOptions, setSelectedOptions] = useState<UserDetails[]>([]);
    const [currentShareList, setCurrentShareList] = useState<string[]>(sharedWith as string[]);
    const messageContent = useRef<string>('');
    const userAccounts = useSelector(selectOfflineReportsUserAccountInfo);
    const getHeaderTextPropsContent = useGetHeaderTextPropsContent();
    const [error, setError] = useState<string>('');
    const getSharingUsers = useCallback(() => {
        let target: SharingTargetOptions = 'user';
        let users: string[] = [];

        isUseWholeAccount
            ? (target = 'account')
            : (users = [...currentShareList, ...selectedOptions.map((opt) => opt.id)]);
        users = users.length > 0 ? users.filter((user) => user !== '') : users;

        return {
            target,
            users,
        };
    }, [selectedOptions, currentShareList, isUseWholeAccount]);

    const getUsersMap = useCallback(() => {
        const result: Dictionary<string> = {};

        userAccounts?.members.map((member) => (result[member.id] = member.username));
        return result;
    }, [userAccounts]);

    const getAvailableUsersList = useCallback(() => {
        const result: UserDetails[] = [];

        userAccounts?.members.map((member) => {
            const found = selectedOptions.find((element) => element.id === member.id);

            if (currentShareList.indexOf(member.id) === -1 && !found) {
                let obj: UserDetails = {
                    id: '',
                    username: '',
                };
                obj.id = member.id;
                obj.username = member.username;
                return result.push(obj);
            }

            return result;
        });
        return result;
    }, [userAccounts, currentShareList, selectedOptions]);

    const usersMap = getUsersMap();
    const availableUsersList = getAvailableUsersList();

    const getHeaderContent = () => {
        if (!React.isValidElement(headerContent)) {
            return getHeaderTextPropsContent(headerContent as HeaderTextProps);
        }
        return headerContent;
    };

    const onMessageContentChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        messageContent.current = e.target.value;
        if (error) {
            if (!isHTMLTags(messageContent.current)) {
                setError('');
            }
        }
    };

    const onSendButtonClick = () => {
        const sharingUsers = getSharingUsers();
        if (isHTMLTags(messageContent.current)) {
            setError('Message must contain plain text only.');
        } else {
            setError('');
            onShareReport(sharingUsers.users, messageContent.current, sharingUsers.target);
        }
    };

    const onAfterClose = () => {
        messageContent.current = '';
    };

    const setWholeAccountSelected = () => {
        !isUseWholeAccount && setIsRecipientsSelectionDone(true);
        setIsSubmitButtonDisabled(false);
        setIsUseWholeAccount(!isUseWholeAccount);
    };

    const handleCancelButtonClick = () => {
        onCancelButtonClick();
    };

    const updateCurrentShareList = (sharedId: string) => {
        if (!isSendingRequest) {
            const index = currentShareList.indexOf(sharedId);

            if (index !== -1) {
                const tempShareList = [...currentShareList];

                tempShareList.splice(index, 1);
                setCurrentShareList(tempShareList);
                setIsSubmitButtonDisabled(false);
            }
        }
    };

    const getUsersMapedItems = useCallback(
        (selectedOptions: string[]) => {
            let result: UserAccountDetailsItem[] = [];

            selectedOptions?.map((opt) => {
                const userItem = userAccounts?.members.find((user) => user.username === opt);
                let item: UserAccountDetailsItem = {
                    id: '',
                    username: '',
                };

                item.id = userItem?.id || '';
                item.username = userItem?.username || '';

                return result.push(item);
            });
            return result;
        },
        [userAccounts],
    );

    const handleChange = (selectedOptionsUsenames: string[]) => {
        selectedOptionsUsenames.length > 0 &&
            !isRecipientsSelectionDone &&
            setIsRecipientsSelectionDone(!isRecipientsSelectionDone);

        isSubmitButtonDisabled && setIsSubmitButtonDisabled(!isSubmitButtonDisabled);

        const selectedOptions = getUsersMapedItems(selectedOptionsUsenames);
        setSelectedOptions(selectedOptions);
    };

    const filteredOptions = availableUsersList.filter((o) => {
        return selectedOptions.map((opt) => {
            return opt.username !== o.username;
        });
    });

    return (
        <Dialog
            className={styles.modal}
            visible={visible}
            centered
            destroyOnClose
            closable={false}
            footer={false}
            width={MODAL_WIDTH}
            onCancel={onCloseModal}
            afterClose={onAfterClose}
        >
            <div className={styles.headerContainer}>{getHeaderContent()}</div>
            <hr className={styles.separator} data-testid="share-modal-separator" />

            {!isRecipientsSelectionDone && (
                <div className={styles.accountSharingWrapper}>
                    <div className={styles.accountSharingText}>
                        <div className={styles.accountSharingTextHead}>Account Access</div>
                        <div>Share with all account members</div>
                    </div>
                    <div className={styles.accountSharingButton}>
                        <Switch
                            checked={isUseWholeAccount}
                            onChange={() => setWholeAccountSelected()}
                            disabled={isSendingRequest}
                            color="#49565d"
                            uid={'xtra_share_switch'}
                        />
                    </div>
                </div>
            )}

            <div
                className={styles.shareRecipientsContainer}
                data-testid="select-ant-dialog-wrapper"
            >
                {!isUseWholeAccount && (
                    <div className={styles.headlineText}>Sharing with users</div>
                )}
                {!isUseWholeAccount && (
                    <Select
                        mode="multiple"
                        placeholder="Add people from your account"
                        value={selectedOptions.map((opt) => opt.username)}
                        onChange={handleChange}
                        style={{ width: '100%' }}
                        dropdownClassName={styles.shareRecipientsSelector}
                    >
                        {filteredOptions.map((item) => (
                            <Select.Option
                                key={item.id}
                                value={item.username}
                                className={styles.shareRecipientsOption}
                            >
                                {item.username}
                            </Select.Option>
                        ))}
                    </Select>
                )}
                {!isRecipientsSelectionDone &&
                    currentShareList.length > 0 &&
                    currentShareList[0].toString() !== '' && (
                        <div className={styles.shareWithListWrapper}>
                            <div className={styles.headlineText}>Shared with</div>
                            <div className={styles.shareWithList}>
                                {currentShareList.map((sharedId: string, idx) => (
                                    <div
                                        key={`shared_item_id_${sharedId}`}
                                        className={styles.shareWithListItemWrapper}
                                    >
                                        <div className={styles.shareWithListItem}>
                                            <div>{usersMap[sharedId]}</div>
                                            <span onClick={() => updateCurrentShareList(sharedId)}>
                                                Remove
                                            </span>
                                        </div>
                                        {idx < currentShareList.length - 1 && (
                                            <hr className={styles.smallSeparator} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                {isRecipientsSelectionDone && (
                    <>
                        {isUseWholeAccount && (
                            <div className={styles.headlineText}>
                                Share with all account members
                            </div>
                        )}
                        <textarea
                            maxLength={MESSAGE_MAX_LENGTH}
                            placeholder={'Message'}
                            onChange={onMessageContentChange}
                            className={styles.textArea}
                            aria-label={'Add email message'}
                            disabled={isSendingRequest}
                        />
                        <p className={classnames(styles.errorText, { [styles.showError]: error })}>
                            {error}
                        </p>
                    </>
                )}
            </div>
            <div className={styles.buttonsContainer}>
                <Button
                    onClick={handleCancelButtonClick}
                    className={styles.button}
                    disabled={isSendingRequest}
                >
                    Cancel
                </Button>
                <Button
                    className={styles.button}
                    type={'brand-primary-full'}
                    disabled={isSubmitButtonDisabled || !!error}
                    onClick={onSendButtonClick}
                    loading={isSendingRequest}
                >
                    {isRecipientsSelectionDone ? 'Share' : 'Done'}
                </Button>
            </div>
        </Dialog>
    );
};
